import { CenterModel } from '@/models/center';
import { DoctorModel } from '@/models/doctor';
import { PatientModel } from '@/models/patient';
import { EventModel, EventType, EventStatus } from '@/models/event';
import { UserModel } from '@/models/user';
import { InvitationModel } from '@/models/invitation';
import { AxiosRequestConfig } from 'axios';
import { StockItemModel, StockItemStatsModel, StockItemStatus, StockCreationModel, StockBatchStatusUpdateModel } from '@/models/stock';
import { DrugModel } from '@/models/drug';
import { DrugGroupModel } from '@/models/drug_group';
import { EventStatModel } from '@/models/stat';

export enum OrderByMode {
  Asc = 'asc',
  Desc = 'desc',
}

export type ApiRequestConfig = AxiosRequestConfig & {
  skipHeaders?: boolean;
  skipCenterHeader?: boolean;
  hasJsonData?: boolean;
  overrideCenterId?: string;
}

export interface IApiService {
  get<T>(path: string, opts?: ApiRequestConfig): Promise<T>;
  patch<T>(path: string, data?: object, opts?: ApiRequestConfig): Promise<T>;
  post<T>(path: string, body?: object, opts?: ApiRequestConfig): Promise<T>;
  request<T>(path: string, opts?: ApiRequestConfig): Promise<T>;
}

export type AuthenticateOptions = {
  email: string;
  password: string;
}

export type AuthenticateResponse = {
  token: string;
  user: any;
  payload: {
    $userId: string;
    $centerId: string | null;
    $doctorId: string | null;
    $patientId: string | null;
    $isManager: boolean;
    $isPrimary: boolean;
    $isDeveloper: boolean;
  };
}

export type RecoveryResponse = {
  key: string;
  file: string;
}

export interface IAuthService {
  authenticate({ email, password }: AuthenticateOptions): Promise<string>;
  changeEmail(newEmail: string): Promise<void>;
  changePassword(oldPassword: string, newPassword: string): Promise<void>;
  logout(): Promise<void>,
  extendRefreshToken(): Promise<string>;
  loadSession(): Promise<void>;
  cacheMetadata(): Promise<void>;
  passwordResetRequest(email: string): Promise<void>;
  passwordReset(token: string, password: string, recoveryKey: string): Promise<void>;
  signUrl(url: string): Promise<string>;
  fork(): Promise<string>;
  recovery(): Promise<RecoveryResponse>;
  register(token: string, code: string, password: string): Promise<UserModel>;
}

export interface ICenterService {
  getMany(page: number, size: number): Promise<[CenterModel[], number]>;
  getById(id: string): Promise<CenterModel>;
  create(data: Partial<CenterModel>): Promise<CenterModel>;
  update(id: string, data: Partial<CenterModel>): Promise<CenterModel>;
}

export interface IDoctorService {
  getMany(page: number, size: number): Promise<[any[], number]>;
  getById(id: string): Promise<DoctorModel>;
  create(data: Partial<DoctorModel>): Promise<DoctorModel>;
  update(id: string, data: Partial<DoctorModel>): Promise<DoctorModel>;
}

export type EventFilter = {
  sortBy?: Map<string, boolean> | null;
  patientId?: string | null;
  doctorId?: string | null;
  types?: EventType[] | null;
  statuses?: EventStatus[] | null;
  searchQuery?: string | null;
  from?: string | null;
  until?: string | null;
}

export type EventExportFilter = {
  fields: string[];
  patientIds?: string[] | null;
  doctorIds?: string[] | null;
  eventTypes?: EventType[] | null;
  eventStatuses?: EventStatus[] | null;
  from?: string | null;
  until?: string | null;
  socialSecurityNo?: string | null;
}

export enum EventOrderBy {
  $occuredAt = '$occuredAt',
  $createdAt = '$createdAt',
  $updatedAt = '$updatedAt',
  type = 'type',
  satus = 'satus',
}

export interface IEventService {
  getMany(
    page: number,
    size: number,
    filter?: EventFilter,
    orderBy?: Map<EventOrderBy, OrderByMode>,
  ): Promise<[EventModel[], number]>;
  update(id: string, data: object): Promise<EventModel>
}

export type DrugFilter = {
  drugGroupId?: string | null;
  searchQuery?: string | null;
}
export enum DrugOrderBy {}

export interface IDrugService {
  getMany(
    page: number,
    size: number,
    filter?: DrugFilter,
    orderBy?: Map<DrugOrderBy, OrderByMode>,
  ): Promise<[DrugModel[], number]>;
  getById(id: string): Promise<DrugModel>;
}

export type DrugGroupFilter = {
  searchQuery?: string | null;
}
export enum DrugGroupOrderBy {}

export interface IDrugGroupService {
  getMany(
    page: number,
    size: number,
    filter?: DrugGroupFilter,
    orderBy?: Map<DrugGroupOrderBy, OrderByMode>,
  ): Promise<[DrugGroupModel[], number]>;
}

export type StockFilter = {
  patientId?: string | null;
  lotId?: string | null;
  drugId?: string | null;
  statuses?: StockItemStatus[] | null;
}

export enum StockOrderBy {
  '$possiblyUsedAt' = '$possiblyUsedAt',
  '$createdAt' = '$createdAt',
  '$updatedAt' = '$updatedAt',
  'status' = 'status'
}

export interface IStockService {
  getMany(
    page: number,
    size: number,
    filter?: StockFilter,
    orderBy?: Map<StockOrderBy, OrderByMode>,
  ): Promise<[StockItemModel[], number]>;
  stats(
    page: number,
    size: number,
    filter?: StockFilter,
    groupByLotId?: boolean,
  ): Promise<[StockItemStatsModel[], number]>;
  create(data: StockCreationModel): Promise<StockItemModel[]>;
  batchUpdate(input: StockBatchStatusUpdateModel[]): Promise<void>;
  return(id: string): Promise<StockItemModel>;
}

export type EventStatFilter = {
  centerIds?: string[] | null;
  patientIds?: string[] | null;
  doctorIds?: string[] | null;
  eventStatuses?: string[] | null;
  eventTypes?: string[] | null;
  from?: string | null;
  until?: string | null;
  socialSecurityNo?: string | null;
}

export type EventStatExportFilter = EventStatFilter & { fields?: string[] | null; }

export interface IStatService {
  getManyEvent(
    page: number,
    size: number,
    filter?: EventStatFilter
  ): Promise<[EventStatModel[], number]>;
  getExport(
    page: number,
    size: number,
    filter: EventStatFilter,
    format: string,
    centerId?: string,
  ): Promise<string>;
}

export enum UserType {
  Patients = 'patients',
  Doctors = 'doctors',
}

export interface IUserService {
  create(type: UserType, $id: any): Promise<UserModel>;
}

export interface IInvitationService {
  create(invitation: InvitationModel): Promise<InvitationModel>;
  verify(id: string, code: string): Promise<boolean>
}

export interface IPatientService {
  getMany(page: number, size: number): Promise<[PatientModel[], number]>;
  getById(id: string): Promise<PatientModel>;
  create(data: Partial<PatientModel>): Promise<PatientModel>;
  updateById(id: string, data: Partial<PatientModel>): Promise<PatientModel>;
  reset(id: string): Promise<void>;
  recovery(id: string): Promise<string>;
}

export type ConstantValue = {value: string, label: string};
export interface ConstantDescriber {
  event: {
    type: ConstantValue[],
    injection_reason: ConstantValue[],
    bleeding:{
      type: ConstantValue[],
      reason: ConstantValue[],
      body_side: ConstantValue[],
      body_section: ConstantValue[],
      body_area: ConstantValue[]
    },
    hjhs: ConstantValue[],
    inspection: ConstantValue[]
  },
  patient: {
    associated_disease_condition: ConstantValue[],
    blood_type: ConstantValue[],
    factor_activity: ConstantValue[],
    treatment_method: ConstantValue[],
    target_joints: ConstantValue[],
    target_joint_types: ConstantValue[],
    movement_activity: ConstantValue[],
  }
}
